import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import axios from "axios";
import { setBodyOverflow } from "../helpers/modal.js";
import CreateExpenseButton from "./CreateExpenseButton";
import CreateExpenseModal from "./CreateExpenseModal";
import UpdateExpenseModal from "./UpdateExpenseModal";
import ScrollToTopButton from "./ScrollToTopButton";
import { preventLinkDefault } from "../helpers/links";

const categoryColors = [
  "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300",
  "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300",
];

const ExpensesList = ({
  categories = [],
  supportedCurrencies = [],
  currency,
  expensesPath,
  onBalanceUpdate,
  month,
  useMonth = false,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [currentExpenseId, setCurrentExpenseId] = useState(null);
  const [currentExpense, setCurrentExpense] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [page, setPage] = useState(1);
  const [pagy, setPagy] = useState({ next: true });

  const formatDate = (dateString) => {
    const options = { day: "2-digit", month: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const fetchData = (pageOverride) => {
    const finalPage = pageOverride || page;
    let url = `${expensesPath}?page=${finalPage}`;
    url = useMonth ? `${url}&month=${month}` : url;
    axios
      .get(url, {
        headers: ReactOnRails.authenticityHeaders({}),
      })
      .then((response) => {
        const { data, pagy, total_balance } = response.data;
        setPagy(pagy);
        const result = finalPage === 1 ? data : [...expenses, ...data];
        setExpenses(result);
        onBalanceUpdate(total_balance);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const openUpdateModal = (id) => {
    setBodyOverflow(true);
    setShowUpdateModal(true);
    setCurrentExpenseId(id);
  };

  const closeUpdateModal = () => {
    setBodyOverflow(false);
    setShowUpdateModal(false);
    setCurrentExpenseId(null);
  };

  useEffect(() => {
    if (page > 1) {
      fetchData();
    }
  }, [page]);

  useEffect(() => {
    setPage(1);
    fetchData(1);
  }, [month]);

  useEffect(() => {
    if (currentExpenseId) {
      setCurrentExpense(
        expenses.find((expense) => expense.id === currentExpenseId)
      );
    } else {
      setCurrentExpense(null);
    }
  }, [currentExpenseId]);

  const onItemCreate = (newValue, newTotalValue) => {
    onBalanceUpdate(newTotalValue);
    console.log("adding", newValue, newTotalValue);
    setExpenses([newValue, ...expenses]);
  };

  const onItemUpdate = (newValue, newTotalValue) => {
    onBalanceUpdate(newTotalValue);
    console.log("updating", newValue, newTotalValue);
    setExpenses(
      expenses.map((expense) =>
        expense.id === newValue.id ? newValue : expense
      )
    );
  };

  const onItemDelete = (id, newTotalValue) => {
    onBalanceUpdate(newTotalValue);
    console.log("deleting", id, newTotalValue);
    setExpenses(expenses.filter((expense) => expense.id !== id));
  };

  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  };

  const categoryColor = (categoryName) => {
    const index = Math.abs(hashCode(categoryName)) % categoryColors.length;
    return categoryColors[index];
  };

  const openCreateModal = (event) => {
    preventLinkDefault(event);
    setBodyOverflow(true);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setBodyOverflow(false);
    setShowCreateModal(false);
  };

  return (
    <div className="mt-4 z-10">
      <InfiniteScroll
        dataLength={expenses.length} //This is important field to render the next data
        next={() => setPage(page + 1)}
        hasMore={!!pagy.next}
        loader={<h4 className="text-center text-gray-900 mt-16">Loading...</h4>}
        endMessage={
          <p className="text-center font-medium text-gray-900 whitespace-nowrap dark:text-white mt-4">
            {expenses.length > 0
              ? "Yay! You have seen it all"
              : "No expenses yet"}
          </p>
        }
      >
        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="pl-4 py-3">
                Amount
              </th>
              <th scope="col" className="px-2 py-3">
                Comment
              </th>
              <th scope="col" className="pr-4 py-3">
                Date
              </th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense) => (
              <tr
                key={expenses.id}
                onClick={() => openUpdateModal(expense.id)}
                className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700"
              >
                <th
                  scope="row"
                  className="pl-4 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                >
                  {expense.total_balance_formatted}
                </th>
                <th
                  scope="row"
                  className="px-2 py-4 font-medium text-gray-900 dark:text-white"
                >
                  {expense.category && (
                    <>
                      <span
                        className={`${categoryColor(
                          expense.category.name
                        )} mb-2 inline-block bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300`}
                      >
                        {expense.category.name}
                      </span>
                    </>
                  )}
                  {expense.comment && <p>{expense.comment}</p>}
                </th>
                <th
                  scope="row"
                  className="py-4 pr-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                >
                  {formatDate(expense.created_at)}
                </th>
              </tr>
            ))}
          </tbody>
        </table>
      </InfiniteScroll>

      <br />
      <br />
      <CreateExpenseModal
        month={month}
        useMonth={useMonth}
        categories={categories}
        defaultCurrency={currency}
        supportedCurrencies={supportedCurrencies}
        expensesPath={expensesPath}
        visible={showCreateModal}
        onClose={closeCreateModal}
        onSuccess={onItemCreate}
      />
      <UpdateExpenseModal
        month={month}
        useMonth={useMonth}
        categories={categories}
        supportedCurrencies={supportedCurrencies}
        currentExpense={currentExpense}
        expensesPath={expensesPath}
        visible={showUpdateModal}
        onClose={closeUpdateModal}
        onSuccess={onItemUpdate}
        onDelete={onItemDelete}
      />
      <CreateExpenseButton onClick={openCreateModal} />
      <ScrollToTopButton />
    </div>
  );
};

ExpensesList.propTypes = {
  categories: PropTypes.array,
  currency: PropTypes.string.isRequired,
  expensesPath: PropTypes.string.isRequired,
  onBalanceUpdate: PropTypes.func.isRequired,
  month: PropTypes.string,
  useMonth: PropTypes.bool,
  supportedCurrencies: PropTypes.array,
};

export default ExpensesList;
